// Application-wide custom events for cross-component communication
// Use these instead of Redux when you need to notify multiple independent
// components about ephemeral events that don't represent application state

export const APP_EVENTS = {
  APPLICANT_TRANSITIONED: 'applicant:transitioned',
  WX_COPILOT_APPLICANTS_INVALIDATED: 'wx-copilot:applicants-invalidated',
  WX_COPILOT_WORKFLOW_INVALIDATED: 'wx-copilot:workflow-invalidated',
} as const;

export interface ApplicantTransitionedDetail {
  applicantId: string;
  stageId: string;
}

export const dispatchApplicantTransitioned = (
  detail: ApplicantTransitionedDetail,
): void => {
  window.dispatchEvent(
    new CustomEvent(APP_EVENTS.APPLICANT_TRANSITIONED, { detail }),
  );
};

export const addApplicantTransitionedListener = (
  handler: (event: CustomEvent<ApplicantTransitionedDetail>) => void,
): (() => void) => {
  window.addEventListener(
    APP_EVENTS.APPLICANT_TRANSITIONED,
    handler as EventListener,
  );
  return () => {
    window.removeEventListener(
      APP_EVENTS.APPLICANT_TRANSITIONED,
      handler as EventListener,
    );
  };
};

export interface ApplicantsInvalidatedDetail {
  applicantIds: string[];
}

// Type definitions for the tool result structure
interface ToolResultContent {
  type?: string;
  text: string;
}

export interface ProcessApplicantsToolResultCallbacks {
  onSuccess?: (applicantIds: string[]) => void;
  onError?: (error: Error) => void;
}

export interface ProcessApplicantsToolResultReturn {
  applicantIds: string[];
}

// Extract the text content array from the tool result.
const getTextContents = (value: unknown): ToolResultContent[] | null => {
  // Direct array: event.result is already [{type, text}, ...]
  if (Array.isArray(value)) return value as ToolResultContent[];

  if (typeof value !== 'object' || value === null) return null;
  const obj = value as Record<string, unknown>;

  // Wrapped: { result: [...] } or { content: [...] }
  if (Array.isArray(obj.result)) return obj.result as ToolResultContent[];
  if (Array.isArray(obj.content)) return obj.content as ToolResultContent[];

  return null;
};

// Extract applicant IDs from parsed tool result JSON.
// Caller is responsible for gating on `shouldInvalidate` first.
// Handles known shapes:
//   1. Single:  { applicant: { external_id } }
//   2. Batch:   { applicant_ids: [...] }
const extractApplicantIds = (value: unknown): string[] | null => {
  if (typeof value !== 'object' || value === null) return null;
  const obj = value as Record<string, unknown>;

  // Single applicant: { applicant: { external_id } }
  if (
    'applicant' in obj &&
    typeof obj.applicant === 'object' &&
    obj.applicant !== null
  ) {
    const applicant = obj.applicant as Record<string, unknown>;
    if (typeof applicant.external_id === 'string' && applicant.external_id) {
      return [applicant.external_id];
    }
    return null;
  }

  // Batch: { applicant_ids: [...] }
  if (Array.isArray(obj.applicant_ids)) {
    const ids = obj.applicant_ids.filter(
      (id: unknown): id is string => typeof id === 'string' && id.length > 0,
    );
    return ids.length > 0 ? ids : null;
  }

  return null;
};

export const createApplicantsInvalidatedEvent = (
  applicantIds: string[],
): CustomEvent<ApplicantsInvalidatedDetail> =>
  new CustomEvent(APP_EVENTS.WX_COPILOT_APPLICANTS_INVALIDATED, {
    detail: { applicantIds },
  });

export const dispatchApplicantsInvalidated = (applicantIds: string[]): void => {
  window.dispatchEvent(createApplicantsInvalidatedEvent(applicantIds));
};

export const addApplicantsInvalidatedListener = (
  handler: (event: CustomEvent<ApplicantsInvalidatedDetail>) => void,
): (() => void) => {
  window.addEventListener(
    APP_EVENTS.WX_COPILOT_APPLICANTS_INVALIDATED,
    handler as EventListener,
  );
  return () => {
    window.removeEventListener(
      APP_EVENTS.WX_COPILOT_APPLICANTS_INVALIDATED,
      handler as EventListener,
    );
  };
};

// --- Invalidation helpers (copilot → UI refresh) ---

// Check if a tool result includes `invalidate: true` (set by write operations).
export const shouldInvalidate = (result: unknown): boolean => {
  const textContents = getTextContents(result);
  if (!textContents?.length) return false;

  const firstContent = textContents[0];
  if (!firstContent || typeof firstContent.text !== 'string') return false;

  try {
    const raw: unknown = JSON.parse(firstContent.text);
    return (
      typeof raw === 'object' &&
      raw !== null &&
      (raw as Record<string, unknown>).invalidate === true
    );
  } catch {
    return false;
  }
};

export const dispatchWorkflowInvalidated = (): void => {
  window.dispatchEvent(
    new CustomEvent(APP_EVENTS.WX_COPILOT_WORKFLOW_INVALIDATED),
  );
};

export const addWorkflowInvalidatedListener = (
  handler: () => void,
): (() => void) => {
  window.addEventListener(
    APP_EVENTS.WX_COPILOT_WORKFLOW_INVALIDATED,
    handler as EventListener,
  );
  return () => {
    window.removeEventListener(
      APP_EVENTS.WX_COPILOT_WORKFLOW_INVALIDATED,
      handler as EventListener,
    );
  };
};

// Parse and dispatch applicant tool results.
// Handles direct arrays, wrapped objects, and all JSON shapes.
export const processApplicantsToolResult = (
  result: unknown,
  callbacks: ProcessApplicantsToolResultCallbacks = {},
): ProcessApplicantsToolResultReturn | null => {
  try {
    const textContents = getTextContents(result);
    if (!textContents?.length) return null;

    const firstContent = textContents[0];
    if (!firstContent || typeof firstContent.text !== 'string') return null;

    const raw: unknown = JSON.parse(firstContent.text);
    const applicantIds = extractApplicantIds(raw);

    if (applicantIds && applicantIds.length > 0) {
      dispatchApplicantsInvalidated(applicantIds);

      if (callbacks.onSuccess) {
        callbacks.onSuccess(applicantIds);
      }

      return { applicantIds };
    }

    return null;
  } catch (error) {
    if (callbacks.onError && error instanceof Error) {
      callbacks.onError(error);
    }
    return null;
  }
};
